import { useCallback, useEffect, useMemo } from 'react';
import { useBulkBroadcastActionsPermission } from 'src/components/common/BulkActions/BulkBroadcastActions/useBulkBroadcastActionsPermission';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useCommandMenuContext } from 'src/contexts/CommandMenuContext/CommandMenuContext';
import { DialogId } from 'src/contexts/DialogContext/DialogContext';
import { useDialog } from 'src/contexts/DialogContext/useDialog';
import { BulkAddListingAdminHoldDialog } from 'src/dialogs/BulkEdits/BulkAddListingAdminHoldDialog';
import { useEventCallback } from 'src/hooks/useEventCallback';
import { ContentId } from 'src/utils/constants/contentId';
import { SQL_MAX_DATEITME } from 'src/utils/dateTimeUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  BulkEditListingClient,
  BulkEditPreviewWithDetails,
  CommandOptionType,
  Event,
  ListingQuery,
} from 'src/WebApiController';

import { INVENTORY_BULK_UPDATE_ADMIN_HOLD_UPDATE_KEY } from '../../InventoryActionDropdown.constants';
import { BulkActionItem } from '../types';
import { LaunchAddHold } from './LaunchAddHold';

export const useBulkAddHoldAction = (
  filterQueryWithEventIds: ListingQuery,
  setIsLoading: (v: boolean) => void,
  isLoading?: boolean,
  event?: Event
) => {
  const { registerCommand, unregisterCommand } = useCommandMenuContext();
  const { hasAdminHoldPermission } = useBulkBroadcastActionsPermission();

  const { activeAccountWebClientConfig } = useAppContext();
  const updateKey = event
    ? `adminHold-event-${event.virtId}`
    : INVENTORY_BULK_UPDATE_ADMIN_HOLD_UPDATE_KEY;
  const {
    refreshCatalog,
    eventsExpansion: { setListItemExpansion, refreshExpandedListItems },
  } = useCatalogDataContext();
  const { showErrorDialog } = useClientLogContext();

  const {
    openDialog: openBulkAddListingAdminHold,
    closeDialog: closeBulkAddListingAdminHold,
  } = useDialog(
    DialogId.BulkAddListingAdminHold,
    BulkAddListingAdminHoldDialog
  );

  const onSubmitAddHold = useCallback(
    async (
      adminHoldExpirationDate: Date | null,
      adminHoldNotes: string | null,
      supportBackgroundProcess?: boolean,
      onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
      preview?: BulkEditPreviewWithDetails
    ) => {
      setIsLoading(true);
      if (onPreviewReceived) {
        await tryInvokeApi(
          async () => {
            const preview = await new BulkEditListingClient(
              activeAccountWebClientConfig
            ).bulkUpdateAdminHoldPreview(filterQueryWithEventIds);

            onPreviewReceived(preview);
          },
          (error) => {
            showErrorDialog('bulkUpdateAdminHoldPreview', error, {
              trackErrorData: { filterQueryWithEventIds, addHold: true },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      } else {
        await tryInvokeApi(
          async () => {
            const succeeded = await new BulkEditListingClient(
              activeAccountWebClientConfig
            ).bulkUpdateAdminHold(
              preview!.preview,
              (adminHoldExpirationDate ?? SQL_MAX_DATEITME).toISOString(),
              adminHoldNotes,
              updateKey,
              supportBackgroundProcess
            );
            if (!supportBackgroundProcess) {
              if (succeeded) {
                if (!event) {
                  await refreshCatalog();
                }
                await refreshExpandedListItems();
              }

              closeBulkAddListingAdminHold();
            }
          },
          (error) => {
            showErrorDialog(
              'BulkEditListingClient.bulkUpdateAdminHold',
              error,
              {
                trackErrorData: {
                  adminHoldExpirationDate,
                  adminHoldNotes,
                  preview,
                  addHold: true,
                },
              }
            );
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      }
    },
    [
      setIsLoading,
      activeAccountWebClientConfig,
      filterQueryWithEventIds,
      showErrorDialog,
      updateKey,
      closeBulkAddListingAdminHold,
      event,
      refreshExpandedListItems,
      refreshCatalog,
    ]
  );

  const openDialogStableRef = useEventCallback(() => {
    if (event) {
      setListItemExpansion(true, [event.virtId]);
    }
    openBulkAddListingAdminHold({
      event,
      updateKey,
      onOkay: onSubmitAddHold,
      onClosed: () => {
        setIsLoading(false);
      },
      onCancel: closeBulkAddListingAdminHold,
    });
  });

  useEffect(() => {
    registerCommand({
      command: {
        id: CommandOptionType.ListingBulkEditAddHold,
        onSelect: openDialogStableRef,
      },
      disabled: !hasAdminHoldPermission,
    });

    return () => {
      unregisterCommand({
        commandId: CommandOptionType.ListingBulkEditAddHold,
        disabled: !hasAdminHoldPermission,
      });
    };
  }, [
    hasAdminHoldPermission,
    openDialogStableRef,
    registerCommand,
    unregisterCommand,
  ]);

  const onClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (event) {
        setListItemExpansion(true, [event.virtId]);
      }
      openBulkAddListingAdminHold({
        event,
        updateKey,
        onOkay: onSubmitAddHold,
        onClosed: () => {
          setIsLoading(false);
        },
        onCancel: closeBulkAddListingAdminHold,
      });
    },
    [
      closeBulkAddListingAdminHold,
      event,
      onSubmitAddHold,
      openBulkAddListingAdminHold,
      setIsLoading,
      setListItemExpansion,
      updateKey,
    ]
  );

  const actionItems = useMemo<BulkActionItem[]>(() => {
    if (!hasAdminHoldPermission) {
      return [];
    }
    return [
      {
        title: ContentId.PutOnHold,
        onClick,
        disabled: isLoading,
      },
    ];
  }, [hasAdminHoldPermission, isLoading, onClick]);

  return {
    dropDown: (
      <LaunchAddHold key="AddHold" onClick={onClick} disabled={isLoading} />
    ),
    actionItems,
  };
};
